import { useConfigurator } from '@/app/store/configurator/hooks/use-configurator';
import { SECTIONS_LEVEL_COUNTS } from '@/shared/constants/configurator';
import { Shelf } from '@/shared/types/configurator';
import { Expanded } from '@/shared/ui/expanded/expanded';
import { Select } from '@/shared/ui/select/select';
import { useConfiguratorData } from '@/widgets/configurator';
import { PencilIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './front-section-editor.module.css';
import { FrontSectionEditorProps } from './front-section-editor.props';

export const FrontSectionEditor = ({
	section,
	sectionIndex,
	removeButtonVisible,
}: FrontSectionEditorProps) => {
	const { removeSection } = useConfigurator();

	return (
		<div className={styles.wrapper}>
			<Expanded
				triggerContent={
					<div className={styles.editButton}>
						<PencilIcon size={15} color="White" />
					</div>
				}
				content={
					<FrontSectionEditorContent section={section} sectionIndex={sectionIndex} />
				}
			/>
			{removeButtonVisible && (
				<button className={styles.removeButton} onClick={() => removeSection(sectionIndex)}>
					<XIcon color="white" className={styles.removeButtonIcon} size={16} />
				</button>
			)}
		</div>
	);
};

const FrontSectionEditorContent = ({
	section,
	sectionIndex,
}: Omit<FrontSectionEditorProps, 'removeButtonVisible'>) => {
	const { t } = useTranslation();
	const { state, updateSections } = useConfigurator();
	const { data } = useConfiguratorData();

	const heightOptions = (data?.heights?.[state.material] || []).map((height) => ({
		label: `${height} ${t('units.mm')}`,
		value: height,
	}));
	const widthOptions = (data?.widths || []).map((width) => ({
		label: `${width} ${t('units.mm')}`,
		value: width,
	}));
	const depthOptions = (data?.depths || []).map((depth) => ({
		label: `${depth} ${t('units.mm')}`,
		value: depth,
	}));
	const levelsOptions = SECTIONS_LEVEL_COUNTS.map((level) => ({
		label: level.toString(),
		value: level,
	}));

	const handleUpdateLevelsCount = (levels: number) => {
		if (levels > section.selfs.length) {
			const newShelvesCount = levels - section.selfs.length;
			const newSelfs = Array.from({ length: newShelvesCount }).map<Shelf>(() => ({
				deck: section.selfs[section.selfs.length - 1]?.deck,
			}));

			updateSections([sectionIndex], {
				selfs: [...section.selfs, ...newSelfs],
			});
		} else {
			const newSelfs = section.selfs.slice(0, levels);
			updateSections([sectionIndex], { selfs: newSelfs });
		}
	};

	return (
		<div className={styles.editorContentWrapper}>
			<Select
				value={section.height}
				options={heightOptions}
				label={t('size.height.sectionTitle')}
				onChange={(height) => updateSections([sectionIndex], { height })}
			/>
			<Select
				value={section.width}
				options={widthOptions}
				label={t('size.width.sectionTitle')}
				onChange={(width) => updateSections([sectionIndex], { width })}
			/>
			<Select
				value={section.depth}
				options={depthOptions}
				label={t('size.depth.sectionTitle')}
				onChange={(depth) => updateSections([sectionIndex], { depth })}
			/>
			<Select
				options={levelsOptions}
				value={section.selfs.length}
				label={t('size.levels.title')}
				onChange={handleUpdateLevelsCount}
			/>
		</div>
	);
};
